import { useEffect, useState, useRef } from "react";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Upload, Download, Trash2, FileStack, Database, Settings2, FileWarning } from "lucide-react";
import { toast } from "sonner";

export default function TemplatesPage() {
  const [templates, setTemplates] = useState(null);
  const [staging, setStaging] = useState(null);
  const [meta, setMeta] = useState({ activities: [], doc_types: [] });
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [mapEdit, setMapEdit] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/templates").then(({ data }) => setTemplates(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const seed = async () => {
    try { const { data } = await api.post("/templates/seed-alici"); toast.success(`Alıcı şablonları: ${data.created.length} eklendi, ${data.existed.length} mevcut.`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/templates/upload-stage", fd);
      setMeta({ activities: data.activities, doc_types: data.doc_types });
      setStaging(data.staged.map((s) => ({ ...s, action: s.duplicate_of ? "version" : "new", activity_code: s.guessed_activity, doc_type: s.guessed_doc_type })));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const items = staging.filter((s) => s.staged_id).map((s) => ({ staged_id: s.staged_id, activity_code: s.activity_code, doc_type: s.doc_type, action: s.action }));
      const { data } = await api.post("/templates/commit", { items });
      toast.success(`${data.committed.length} şablon aktarıldı.`);
      setStaging(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCommitting(false); }
  };

  const del = async (id) => { try { await api.delete(`/templates/${id}`); toast.success("Şablon silindi."); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const saveMapping = async () => {
    try {
      await api.put(`/templates/${mapEdit.id}/mapping`, {
        mapping_status: mapEdit.mapping_status, mapping: mapEdit.mapping || {},
        doc_info: { code: mapEdit.code || "", revision: mapEdit.revision || "" },
        doc_type: mapEdit.doc_type, activity_code: mapEdit.activity_code,
      });
      toast.success("Eşleme güncellendi."); setMapEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const ACT = [{ code: "alici", label: "Alıcı" }, { code: "bosaltan", label: "Boşaltan" }, { code: "gonderen", label: "Gönderen" }, { code: "tasiyan", label: "Taşıyan" }, { code: "dolduran", label: "Dolduran" }, { code: "yukleyen", label: "Yükleyen" }, { code: "paketleyen", label: "Paketleyen" }, { code: "portatif_tank_konteyner", label: "Portatif Tank/Konteyner" }, { code: "general", label: "Genel" }];
  const DT = [{ code: "procedure", label: "Prosedür" }, { code: "list", label: "Liste" }, { code: "checklist", label: "Kontrol Formu" }, { code: "instruction", label: "Talimat" }, { code: "other", label: "Diğer" }];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Şablonlar</h1>
          <p className="text-sm text-slate-500 mt-1">Word/Excel/PDF veya ZIP yükleyin. Yükleme ile otomatik alan eşlemesi ayrıdır; eşlemesi tamamlanmayan şablonlar işaretlenir.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={seed} data-testid="seed-alici-btn"><Database className="h-4 w-4 mr-2" /> Alıcı Şablonlarını Yükle</Button>
          <input ref={fileRef} type="file" multiple accept=".docx,.doc,.xlsx,.xls,.pdf,.zip" className="hidden" onChange={onFiles} data-testid="template-file-input" />
          <Button className="bg-sky-600 hover:bg-sky-700" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="template-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />} Toplu Yükle
          </Button>
        </div>
      </div>

      {/* Staging önizleme */}
      {staging && (
        <Card className="border-sky-300 bg-sky-50/40" data-testid="staging-panel">
          <CardHeader><CardTitle className="font-display text-lg">Aktarım Önizlemesi ({staging.filter((s) => s.staged_id).length} dosya)</CardTitle></CardHeader>
          <CardContent>
            {staging.some((s) => s.error) && <div className="mb-3 text-sm text-rose-600">{staging.filter((s) => s.error).map((s, i) => <div key={i}>{s.error}</div>)}</div>}
            <Table>
              <TableHeader><TableRow><TableHead>Dosya</TableHead><TableHead>Faaliyet</TableHead><TableHead>Tür</TableHead><TableHead>Durum</TableHead><TableHead>İşlem</TableHead></TableRow></TableHeader>
              <TableBody>
                {staging.filter((s) => s.staged_id).map((s, i) => (
                  <TableRow key={s.staged_id} data-testid={`staging-row-${i}`}>
                    <TableCell className="font-medium">{s.filename}</TableCell>
                    <TableCell>
                      <Select value={s.activity_code} onValueChange={(v) => setStaging((arr) => arr.map((x) => x.staged_id === s.staged_id ? { ...x, activity_code: v } : x))}>
                        <SelectTrigger className="h-8 w-40" data-testid={`staging-activity-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{ACT.map((a) => <SelectItem key={a.code} value={a.code}>{a.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select value={s.doc_type} onValueChange={(v) => setStaging((arr) => arr.map((x) => x.staged_id === s.staged_id ? { ...x, doc_type: v } : x))}>
                        <SelectTrigger className="h-8 w-36"><SelectValue /></SelectTrigger>
                        <SelectContent>{DT.map((d) => <SelectItem key={d.code} value={d.code}>{d.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>{s.duplicate_of ? <StatusBadge tone="pending">Aynı ad mevcut</StatusBadge> : <StatusBadge tone="info">Yeni</StatusBadge>}</TableCell>
                    <TableCell>
                      <Select value={s.action} onValueChange={(v) => setStaging((arr) => arr.map((x) => x.staged_id === s.staged_id ? { ...x, action: v } : x))}>
                        <SelectTrigger className="h-8 w-32" data-testid={`staging-action-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">Kopya (yeni)</SelectItem>
                          <SelectItem value="version">Sürüm (değiştir)</SelectItem>
                          <SelectItem value="skip">Atla</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex gap-2 mt-4">
              <Button className="bg-sky-600 hover:bg-sky-700" onClick={commit} disabled={committing} data-testid="staging-commit-btn">{committing && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Onayla ve Aktar</Button>
              <Button variant="outline" onClick={() => setStaging(null)}>İptal</Button>
            </div>
            <p className="text-xs text-slate-500 mt-2">Orijinal dosya ve düzen korunur. Aktarım yalnız bu onaydan sonra yapılır.</p>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {templates === null ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div> :
            templates.length === 0 ? <div className="py-16 text-center text-slate-500"><FileStack className="h-10 w-10 mx-auto mb-3 text-slate-300" /> Henüz şablon yok. "Alıcı Şablonlarını Yükle" veya "Toplu Yükle" ile başlayın.</div> : (
              <Table>
                <TableHeader><TableRow><TableHead>Şablon</TableHead><TableHead>Faaliyet</TableHead><TableHead>Tür</TableHead><TableHead>Eşleme</TableHead><TableHead className="text-right">İşlem</TableHead></TableRow></TableHeader>
                <TableBody>
                  {templates.map((t) => (
                    <TableRow key={t.id} data-testid={`template-row-${t.id}`}>
                      <TableCell className="font-medium">{t.name}<div className="text-xs text-slate-400">{t.original_filename}</div></TableCell>
                      <TableCell>{t.activity_label}</TableCell>
                      <TableCell>{t.doc_type_label}</TableCell>
                      <TableCell>{t.mapping_status === "mapped" ? <StatusBadge tone="active">Eşlendi</StatusBadge> : <StatusBadge tone="pending" testId={`tpl-unmapped-${t.id}`}><FileWarning className="h-3 w-3 mr-1 inline" />Eşleme Eksik</StatusBadge>}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => downloadFile(`/templates/${t.id}/download`, t.original_filename)} data-testid={`tpl-download-${t.id}`}><Download className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setMapEdit({ id: t.id, mapping_status: t.mapping_status, activity_code: t.activity_code, doc_type: t.doc_type, code: t.doc_info?.code, revision: t.doc_info?.revision, mapping: t.mapping })} data-testid={`tpl-mapping-${t.id}`}><Settings2 className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => del(t.id)} data-testid={`tpl-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </CardContent>
      </Card>

      {/* Eşleme düzenleyici (ayrı tutulur; alan/içerik uydurulmaz) */}
      <Dialog open={!!mapEdit} onOpenChange={(o) => !o && setMapEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Alan Eşlemesi</DialogTitle></DialogHeader>
          {mapEdit && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Faaliyet</Label>
                  <Select value={mapEdit.activity_code} onValueChange={(v) => setMapEdit({ ...mapEdit, activity_code: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{ACT.map((a) => <SelectItem key={a.code} value={a.code}>{a.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Tür</Label>
                  <Select value={mapEdit.doc_type} onValueChange={(v) => setMapEdit({ ...mapEdit, doc_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{DT.map((d) => <SelectItem key={d.code} value={d.code}>{d.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Doküman No</Label><Input value={mapEdit.code || ""} onChange={(e) => setMapEdit({ ...mapEdit, code: e.target.value })} placeholder="(boş bırakılabilir)" /></div>
                <div className="space-y-1.5"><Label>Revizyon No</Label><Input value={mapEdit.revision || ""} onChange={(e) => setMapEdit({ ...mapEdit, revision: e.target.value })} placeholder="(boş bırakılabilir)" /></div>
              </div>
              <div className="space-y-1.5"><Label>Eşleme Durumu</Label>
                <Select value={mapEdit.mapping_status} onValueChange={(v) => setMapEdit({ ...mapEdit, mapping_status: v })}>
                  <SelectTrigger data-testid="mapping-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mapped">Eşlendi (çıktı hazır)</SelectItem>
                    <SelectItem value="unmapped">Eşleme Eksik</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-slate-500">Eşleme yalnızca doküman bilgisi ve bağlanacak alanları tanımlar. İçerik/alan UYDURULMAZ; yalnızca dosya yüklenmiş olması otomatik çıktı hazır anlamına gelmez.</p>
              {mapEdit.mapping?.fields?.length > 0 && (
                <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                  <div className="text-xs font-semibold text-slate-600 mb-1">Tanımlı Alan Eşlemeleri</div>
                  {mapEdit.mapping.fields.map((f, i) => (
                    <div key={i} className="text-xs text-slate-600 flex justify-between" data-testid={`mapping-field-${i}`}>
                      <span>{f.label || f.key}</span><span className="font-mono-tmgd text-slate-400">{f.target}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button className="bg-sky-600 hover:bg-sky-700" onClick={saveMapping} data-testid="mapping-save-btn">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
